import { memo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Flag, Timer } from 'lucide-react';
import type { Capture } from '@draughts/shared';
import { countryCodeToFlag } from '@draughts/shared';
import { useGameStore } from '../../store/game-store';
import { countryName } from '../../utils/country';
import { isGuestUsername } from '../../utils/guest';
import { isDisplayableRating } from '../../utils/rating';
import { UserAvatar } from './UserAvatar';
import { ClockMonospace } from './ClockMonospace';
import { QuadranteOrologio } from './orologio-contesto';
import { CapturedPieces } from './CapturedPieces';
import { useFitsOnOneLine } from './label-fit';
import './PlayerCard.css';

/** Win/loss/draw shown in the clock's slot once the game is over (the clock is
 *  gone by then). Mirrors GameEndOverlay's mapping for visual consistency. */
const RESULT_CHIP = {
  win: { icon: <CheckCircle size={16} aria-hidden="true" />, i18nKey: 'arena.gameEnd.win' },
  loss: { icon: <XCircle size={16} aria-hidden="true" />, i18nKey: 'arena.gameEnd.loss' },
  draw: { icon: <Flag size={16} aria-hidden="true" />, i18nKey: 'arena.gameEnd.draw' },
} as const;

export interface PlayerCardProps {
  username: string;
  rating: number | null;
  variant: 'self' | 'opponent';
  pieceColor: 'white' | 'black';
  /** Avatar preset key (preset icon, else username initial). */
  avatar?: string | null;
  /** ISO 3166-1 alpha-2 country code; renders a flag before the name when set. */
  country?: string | null;
  /**
   * Millisecondi residui, o `null` se la partita non ha orologio.
   *
   * ⚠️ IN PARTITA NON SI USA: la schermata di gioco passa `latoOrologio` e il
   * valore arriva per CONTESTO, perche' cambia quattro volte al secondo e come
   * prop costringerebbe tutto l'albero a ri-renderizzarsi con lui (vedi
   * `orologio-contesto.tsx`, che porta la misura). Resta per chi monta un
   * cartellino fuori da quel contesto — i test, per esempio.
   */
  clockMs?: number | null;
  /** Dice solo SE c'e' un orologio. Stabile fra un tick e l'altro, al contrario
   *  del valore: e' cio' che permette al `memo` a monte di fermarsi. */
  hasClock?: boolean;
  /** Quale dei due quadranti leggere dal contesto. Assente = si usa `clockMs`. */
  latoOrologio?: 'mio' | 'avversario';
  isClockActive: boolean;
  isClockLow: boolean;
  isMyTurn: boolean;
  /**
   * Chi guarda, non chi gioca. Vede i segnali di chi gioca, detti in terza
   * persona: «Muove» su tutt'e due le card e mai «Tocca a te», che a uno
   * spettatore direbbe una cosa falsa; e la caduta su qualunque delle due, non
   * solo su quella dell'avversario — per lui non c'e' un «avversario».
   */
  readOnly?: boolean;
  isOpponentDisconnected?: boolean;
  graceTimeRemaining?: number | null;
  /**
   * L'avversario non si e' mai presentato (torneo): «Non presente» invece di
   * «Disconnesso», che direbbe che c'era ed e' caduto.
   */
  avversarioMaiArrivato?: boolean;
  /** Opponent pieces this player has captured, chronological */
  captured?: Capture[];
  /** Material differential: this player's captures minus the opponent's */
  advantage?: number;
  /** Game-over outcome for THIS player — shown in the clock's slot (which is
   *  empty once the game ends). Null/undefined while the game is in progress. */
  result?: 'win' | 'loss' | 'draw' | null;
  /**
   * The player's real account username, which is NOT always `username`: that
   * one is a display label and may be a localized literal ("You", "Anonymous",
   * "Computer (easy)"). Null/absent means there is no account to open.
   */
  profileUsername?: string | null;
  /** Opens `profileUsername`'s profile. Omit to keep the name non-interactive. */
  onProfileClick?: (username: string) => void;
  /**
   * Un segno subito dopo il nome: qualcosa che vale per tutta la partita e va
   * ricordato mentre si gioca (il ⚡ del boost d'arena). Sta lì e non nella
   * barra dei comandi perché quella la si guarda solo quando si decide, mentre
   * il nome è sempre sotto gli occhi.
   */
  nameBadge?: ReactNode;
  /**
   * Prende il posto di «Tocca a te» finché è valorizzato: alla cieca è l'annuncio
   * dell'ultima mossa, che deve stare da qualche parte senza allungare la schermata —
   * e questa riga lo spazio ce l'ha già riservato.
   *
   * Il turno, intanto, continua a dirlo il bordo della card (`player-card--active`),
   * che è già il modo in cui la schermata lo comunica: vedi la nota su `readOnly`.
   */
  statusNote?: ReactNode;
  /**
   * Secondi che restano per giocare la PRIMA mossa, o null quando non c'è
   * niente da contare. Lo riceve SOLO la card di chi ha il tratto — l'unico che
   * può muovere — quindi chi deve muovere lo vede sulla propria e chi aspetta
   * su quella dell'altro.
   *
   * A differenza di `statusNote` (solo `self`) e dell'avviso di disconnessione
   * (solo `opponent`) vale per tutt'e due le variant: il fatto che racconta
   * riguarda il giocatore, non chi guarda.
   */
  attesaPrimaMossaSec?: number | null;
  /** Cosa succede allo scadere: annullamento, o sconfitta a tavolino in arena. */
  attesaPrimaMossaEsito?: 'void' | 'forfeit' | null;
  /**
   * Il cartellino sta a testa in giu'.
   *
   * Serve alla partita in presenza, dove chi sta dall'altro lato del tavolo
   * leggeva capovolti nome, pezzi mangiati e soprattutto l'OROLOGIO. Ruota la
   * card intera e non il solo testo: quel che deve arrivare dritto e' tutto il
   * contenuto, quadrante compreso. Il rettangolo occupato non cambia, quindi
   * l'impaginazione non si muove di un pixel a nessuna larghezza.
   */
  ruotata?: boolean;
}

export const PlayerCard = memo<PlayerCardProps>(
  ({
    username,
    rating,
    variant,
    pieceColor,
    avatar,
    country,
    clockMs = null,
    hasClock,
    latoOrologio,
    isClockActive,
    isClockLow,
    isMyTurn,
    readOnly = false,
    isOpponentDisconnected = false,
    graceTimeRemaining = null,
    avversarioMaiArrivato = false,
    captured = [],
    advantage = 0,
    result = null,
    profileUsername = null,
    onProfileClick,
    nameBadge,
    statusNote,
    attesaPrimaMossaSec = null,
    attesaPrimaMossaEsito = null,
    ruotata = false,
  }) => {
    const { t, i18n } = useTranslation();
    const flag = country ? countryCodeToFlag(country) : '';

    const className = [
      'player-card',
      `player-card--${variant}`,
      isMyTurn && 'player-card--active',
      isOpponentDisconnected && 'player-card--disconnected',
      ruotata && 'player-card--ruotata',
      // Marks time-controlled cards so focus mode can collapse them to the clock only.
      (latoOrologio ? Boolean(hasClock) : clockMs !== null) && 'player-card--has-clock',
    ]
      .filter(Boolean)
      .join(' ');

    // Guests and bots (reserved `Guest_` prefix) have a meaningless default
    // rating — and their games are unrated — so we hide the number entirely.
    const showRating = !isGuestUsername(username);
    /*
     * `isDisplayableRating` e non `== null`: la guardia di casa scarta anche lo
     * ZERO, e lo zero e' sempre un numero inventato da un ripiego a monte — era
     * proprio cosi' che l'avversario di un torneo, al rientro, compariva a 0.
     * La sorgente e' riparata; questa e' la rete che impedisce a una sorgente
     * futura di ristampare la stessa bugia.
     */
    const ratingDisplay = isDisplayableRating(rating) ? String(rating) : '—';

    // Bots are deliberately disguised as guests (a `Guest_` name re-rolled on
    // every server boot, `is_bot` never sent to the client). Linking to their
    // profile would blow that cover, so the same prefix that hides the rating
    // also blocks the link.
    const canOpenProfile = Boolean(
      profileUsername && onProfileClick && !isGuestUsername(profileUsername)
    );

    // La scritta del turno è l'ultima cosa a entrare nella riga di stato: su un telefono
    // stretto con un vantaggio materiale grosso lo spazio finisce, va a capo e alza questa
    // card di ~9px rispetto all'altra. Misurata, non dedotta da un breakpoint — quel che
    // resta dipende dal numero di catture e dalla lingua, e una soglia sul viewport non
    // vede né l'una né l'altra cosa.
    //
    // La porta la card di CHI HA IL TRATTO, quale che sia: «Tocca a te» sulla propria,
    // «Muove» sull'altra. Senza la seconda il turno dell'avversario non era detto da
    // nessuna parola — restavano 3px di bordo — e a inizio partita non si capiva a chi
    // tocca (il caso peggiore: una partita senza orologio, dove non c'è nemmeno un
    // orologio attivo a dirlo).
    //
    // A chi GUARDA la diciamo sempre nella forma neutra, «Muove», su tutt'e due le card:
    // «Tocca a te» si rivolgerebbe a lui come se dovesse muovere. Prima non gliela
    // dicevamo affatto, contando su un'intestazione col turno che non c'era piu'.

    /*
     * Il conto della prima mossa. Cede il passo all'avviso di disconnessione,
     * che su questa card vive nello stesso posto: se chi ha il tratto è anche
     * scollegato i due conti finirebbero affiancati, e quello che il giocatore
     * deve leggere è il perché l'altro non muove — non i secondi.
     */
    const mostraAttesa =
      attesaPrimaMossaSec !== null &&
      attesaPrimaMossaSec >= 0 &&
      !(isOpponentDisconnected && (variant === 'opponent' || readOnly));

    /*
     * Il conto NON zittisce la scritta del turno, e qui c'è una decisione che è
     * stata presa due volte.
     *
     * Prima la scritta cedeva al conto: diceva la stessa cosa e in più diceva
     * quanto manca. Ma il conto si vede SOLO prima della prima mossa, cioè
     * proprio a inizio partita — e a inizio partita quel che si vede è un'icona
     * di cronometro e dei secondi su UNA delle due card: a chi tocchi lo si
     * deduce da quale card porta il conto. È il momento di cui l'utente si è
     * lamentato, e cedere lì voleva dire tacere esattamente quando serviva
     * parlare. Ora convivono: «Muove» dice CHI, il conto dice QUANTO MANCA.
     *
     * A spartire la riga, se un giorno non bastasse, ci pensa `useFitsOnOneLine`
     * qui sotto: la scritta è un richiamo e si spegne, il conto è informazione e
     * resta (`flex-shrink: 0` nel foglio di stile).
     */
    const showTurnLabel = isMyTurn;

    // La nota prende il posto della scritta del turno finché c'è. La differenza che conta
    // non è quale delle due si vede, ma COME cedono: la scritta del turno è un richiamo e
    // si può perdere (sotto), la nota è informazione e non si perde — stessa distinzione
    // che `.player-card__disconnected` fa già nel foglio di stile.
    //
    // Al conto invece cede, a differenza della scritta del turno qui sopra: la nota è una
    // mossa in notazione algebrica e vuole la riga intera, mentre «Muove» sono 45px che
    // accanto al conto ci stanno. Ed è un caso di confine del caso di confine — una
    // partita alla cieca, prima della prima mossa libera.
    const showNote = Boolean(statusNote) && !mostraAttesa;

    /**
     * La variante della partita in corso, per il colore del segnalino col tema 'auto'.
     *
     * È LA STESSA FONTE DELLA SCACCHIERA — `GameBoard` legge questo identico selettore e ci
     * risolve i suoi colori — e vale per tutti gli ospiti di `GameScreen`, spettatore
     * compreso, perché anche lo store dello spettatore riversa qui la variante. Prenderla
     * dalla prop `variant` di `GameScreen` no: quella è tipizzata su tre varianti sole e
     * può mancare, e segnalino e scacchiera potrebbero mostrare due pedine diverse.
     */
    const gameVariant = useGameStore((s) => s.variant);

    const turnLabelRef = useRef<HTMLSpanElement>(null);
    const turnLabelFits = useFitsOnOneLine(
      turnLabelRef,
      // La nota entra nella chiave o la misura resterebbe quella del contenuto di prima,
      // in tutti e due i passaggi (nota che arriva, nota che se ne va).
      `${i18n.language}|${isMyTurn}|${advantage}|${captured.length}|${showNote}|${mostraAttesa}`
    );

    return (
      <div className={className} data-testid="player-card">
        <div className="player-card__left">
          <UserAvatar
            username={username}
            avatar={avatar}
            variant={variant}
            pieceColor={pieceColor}
            pieceVariant={gameVariant}
          />
          <div className="player-card__meta">
            <div className="player-card__name-row">
              {flag && (
                <span
                  className="player-card__flag"
                  data-testid="player-card-flag"
                  role="img"
                  aria-label={countryName(country as string, i18n.language)}
                >
                  {flag}
                </span>
              )}
              {canOpenProfile ? (
                <button
                  type="button"
                  className="player-card__username player-card__username--link"
                  data-testid="player-card-username"
                  onClick={() => onProfileClick!(profileUsername!)}
                  aria-label={t('profile.viewProfileOf', { username })}
                  // The card truncates long names (max-width 180px, 110px on
                  // phones); the tooltip keeps the target identifiable.
                  title={username}
                >
                  {username}
                </button>
              ) : (
                <span className="player-card__username" data-testid="player-card-username">
                  {username}
                </span>
              )}
              {nameBadge}
              {showRating && (
                <span className="player-card__rating" data-testid="player-card-rating">
                  {ratingDisplay}
                </span>
              )}
            </div>
            <div className={`player-card__status-row${showNote ? ' player-card__status-row--note' : ''}`}>
              <CapturedPieces captures={captured} advantage={advantage} />
              <span
                ref={turnLabelRef}
                className={[
                  'player-card__turn-indicator',
                  showTurnLabel && !showNote && 'player-card__turn-indicator--active',
                  // Switched off (not unmounted) when the row is too tight — the card's
                  // left border carries the turn cue on its own. MAI per la nota: quella
                  // e' l'unico canale che dice cosa ha giocato l'altro, e sparire in
                  // silenzio le toglierebbe la ragione di esistere.
                  !turnLabelFits && !showNote && 'player-card__turn-indicator--overflow',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-testid="player-card-turn-indicator"
                aria-hidden={!showTurnLabel && !showNote}
              >
                {showNote
                  ? statusNote
                  : showTurnLabel
                    ? variant === 'self' && !readOnly
                      ? t('game.turn.yourTurn', 'Your turn')
                      : t('game.turn.toMove', 'To move')
                    : ' '}
              </span>
              {mostraAttesa && (
                <span
                  className={[
                    'player-card__attesa',
                    attesaPrimaMossaSec! <= 10 && 'player-card__attesa--urgente',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-testid="player-card-attesa-prima-mossa"
                  role="timer"
                  /* La frase intera sta qui: sulla riga ci stanno solo l'icona e
                     i secondi, che a 360px è tutto lo spazio che c'è. */
                  aria-label={t(
                    attesaPrimaMossaEsito === 'forfeit'
                      ? 'game.primaMossa.ariaForfeit'
                      : 'game.primaMossa.ariaVoid',
                    { count: attesaPrimaMossaSec! }
                  )}
                  title={t(
                    attesaPrimaMossaEsito === 'forfeit'
                      ? 'game.primaMossa.ariaForfeit'
                      : 'game.primaMossa.ariaVoid',
                    { count: attesaPrimaMossaSec! }
                  )}
                >
                  <Timer size={13} aria-hidden="true" />
                  {t('game.primaMossa.secondi', { count: attesaPrimaMossaSec! })}
                </span>
              )}
              {isOpponentDisconnected && (variant === 'opponent' || readOnly) && (
                <span className="player-card__disconnected" data-testid="player-card-disconnected">
                  {avversarioMaiArrivato
                    ? t('game.disconnection.notPresent')
                    : t('game.disconnection.disconnected', 'Disconnected')}
                  {graceTimeRemaining != null && graceTimeRemaining > 0 && (
                    <span className="player-card__grace">
                      {' '}
                      ({Math.ceil(graceTimeRemaining / 1000)}s)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        {result ? (
          <div className="player-card__right">
            <span
              className={`player-card__result player-card__result--${result}`}
              data-testid="player-card-result"
            >
              {RESULT_CHIP[result].icon}
              <span className="player-card__result-text">{t(RESULT_CHIP[result].i18nKey)}</span>
            </span>
          </div>
        ) : (latoOrologio ? Boolean(hasClock) : clockMs !== null) ? (
          <div className="player-card__right">
            {latoOrologio
              ? <QuadranteOrologio lato={latoOrologio} isActive={isClockActive} isLow={isClockLow} />
              : <ClockMonospace time={clockMs as number} isActive={isClockActive} isLow={isClockLow} />}
          </div>
        ) : null}
      </div>
    );
  }
);

PlayerCard.displayName = 'PlayerCard';
